import type { FC } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Heading, Spacer, VStack } from '@chakra-ui/react';
import { IconName } from '@shinetools/sunshine-icons';
import { isValid } from 'iban';

import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import {
  MemberInformationFormProps,
  MemberInformationRoleForm,
} from 'features/Team/components/MemberInformationRoleForm';
import type {
  EditableRole,
  MemberInformationRoleFormSchema,
} from 'features/Team/components/MemberInformationRoleForm/schema';
import PayPerSeatMarketingCallout from 'features/Team/components/PayPerSeatMarketingCallout';
import * as Layout from 'layouts/StickyStepperLayout';

import locales from './locales';

const MEMBER_ROLE_FORM_ID = 'MemberRoleFormId';

type MemberRoleLayoutProps = {
  onBackClicked?: () => void;
  onApplyClicked?: (data: MemberInformationRoleFormSchema) => void;
  onCancelClicked?: () => void;
  onRoleChanged?: (role: EditableRole) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  initialRole?: EditableRole;
  selectedRole?: EditableRole;
  isFreeRolesOnly: boolean;
  applyLabel: string;
  applyIcon?: IconName;
  showFreeRolesTag: boolean;
  title: string;
  roleTransitions: MemberInformationFormProps['roleTransitions'];
};

export const MemberRoleLayout: FC<MemberRoleLayoutProps> = ({
  applyIcon,
  applyLabel,
  initialRole,
  isDisabled,
  isFreeRolesOnly,
  isLoading,
  onApplyClicked,
  onBackClicked,
  onCancelClicked,
  onRoleChanged,
  roleTransitions,
  selectedRole,
  showFreeRolesTag,
  title,
}) => {
  const roleChanged = selectedRole !== initialRole;

  const handleBackButtonClick = () => onBackClicked?.();
  const handleCancelButtonClick = () => onCancelClicked?.();
  const handleConfirmButtonClick: SubmitHandler<
    MemberInformationRoleFormSchema
  > = (data) => onApplyClicked?.(data);
  const handleChange = async (role: EditableRole) => {
    onRoleChanged?.(role);
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={handleBackButtonClick} />
        </Layout.HeaderButtonsContainer>
        <Layout.ContentsContainer gap="space-24">
          <VStack align="flex-start" spacing="space-16">
            <Heading size="lg">{title}</Heading>
            {!isFreeRolesOnly ? <PayPerSeatMarketingCallout /> : null}
            <MemberInformationRoleForm
              currentRole={initialRole}
              id={MEMBER_ROLE_FORM_ID}
              isFreeRolesOnly={isFreeRolesOnly}
              onChange={handleChange}
              onSubmit={handleConfirmButtonClick}
              roleTransitions={roleTransitions}
              showFreeRolesTag={showFreeRolesTag}
            />
          </VStack>
        </Layout.ContentsContainer>
        <Layout.FooterContainer>
          <StepperProgressBar />
          <Layout.FooterButtonsContainer>
            <Button
              onClick={handleCancelButtonClick}
              variant="inline-secondary"
            >
              {locales.cancel}
            </Button>
            <Spacer />
            <Button
              form={MEMBER_ROLE_FORM_ID}
              icon={applyIcon}
              isDisabled={!isValid || !roleChanged || isDisabled}
              isLoading={isLoading}
              type="submit"
            >
              {applyLabel}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};
